import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons/faCircleExclamation'
import { Text } from '@mantine/core'
import { useEffect, useState } from 'react'

import { useMutationDeleteItemCategory } from '@flecto/core/saas'
import { Trans, useTranslation } from '@flecto/translations'
import { type CoreItemCategory } from '@flecto/types'
import { Modal, useModal } from '@flecto/ui'
import { useNotifications } from '@flecto/ui'

import { useFlattenItemCategories } from '../../hooks/useFlattenItemCategories'
import { CategoryDeleteForm } from '../CategoryDeleteForm/CategoryDeleteForm'

export type ModalCategoryDeleteProps = {
  category?: CoreItemCategory
}

export function ModalCategoryDelete({ category }: ModalCategoryDeleteProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'profile_inventory' })
  const { categories } = useFlattenItemCategories() || []
  const isSoloCategory = categories.length === 1
  const { showNotification } = useNotifications()
  const { mutateAsync: deleteCategory } = useMutationDeleteItemCategory()
  const hasItemsToReAssign = (category?.items_count || 0) > 0 && !isSoloCategory
  const hasSubCategories =
    !category?.parent_id && (category?.subcategories?.length || 0) > 0
  const { closeModal } = useModal()
  const [isFormValid, setIsFormValid] = useState(false)
  const [isFormLoading, setIsFormLoading] = useState(false)

  const title = isSoloCategory
    ? t('category_delete_modal.title_cannot_delete', {
        replace: {
          category: category?.name,
        },
      })
    : t('category_delete_modal.title', {
        replace: {
          category: category?.name,
        },
      })

  async function deleteItemCategory() {
    try {
      if (category?.internal_id) {
        setIsFormLoading(true)
        await deleteCategory({
          categoryId: category?.internal_id,
        })

        showNotification({
          type: 'success',
          message: t('notification_delete_category_success'),
        })
      }
      closeModal()
    } catch (_) {
      showNotification({
        type: 'error',
        message: t('notification_delete_category_error'),
      })
      closeModal()
    } finally {
      setIsFormLoading(false)
    }
  }

  return (
    <Modal size='md'>
      <Modal.Icon
        color='red'
        icon={faCircleExclamation}
        mb={'md'}
        withCircleBackground
      />
      <Modal.Title>{title}</Modal.Title>
      {isSoloCategory ? (
        <Modal.Cancel>
          {t('category_delete_modal.button_cannot_delete')}
        </Modal.Cancel>
      ) : (
        <Modal.Cancel disabled={isFormLoading} />
      )}
      {!hasItemsToReAssign && hasSubCategories && (
        <Modal.Submit
          color='green'
          preventClose
          type='submit'
          loading={isFormLoading}
          onClick={deleteItemCategory}
        >
          {t('category_delete_modal.button_subcategory')}
        </Modal.Submit>
      )}
      {hasItemsToReAssign && (
        <Modal.Submit
          color='red'
          form='category-delete-form'
          preventClose
          type='submit'
          loading={isFormLoading}
          disabled={!isFormValid}
        >
          {t('category_delete_modal.button')}
        </Modal.Submit>
      )}
      <Modal.Content>
        {hasItemsToReAssign && (
          <Text c='gray.7' mb='md'>
            <Trans
              components={{
                b: <strong />,
              }}
              count={category?.items_count ?? 0}
              i18nKey='profile_inventory.category_delete_modal.delete_description'
              ns='saas'
            />
          </Text>
        )}
        {isSoloCategory && (
          <Text c='gray.7' mb='md'>
            <Trans
              components={{
                b: <strong />,
              }}
              count={category?.items_count ?? 0}
              i18nKey='profile_inventory.category_delete_modal.description_cannot_delete'
              ns='saas'
            />
          </Text>
        )}
        {hasSubCategories && (
          <Text c='gray.7'>
            {t('category_delete_modal.delete_description_parent')}
          </Text>
        )}
        {hasItemsToReAssign && (
          <CategoryDeleteForm
            id='category-delete-form'
            onSuccess={closeModal}
            onError={closeModal}
            category={category}
            onValidationChange={setIsFormValid}
            onLoading={setIsFormLoading}
          />
        )}
      </Modal.Content>
    </Modal>
  )
}
